import {
  CONNECT_CLIENT_OPTIONS,
  type ConnectClient,
} from "@/app/(marketing)/integrations/constants";

/**
 * The URL hash as the one store for which client's walkthrough is showing.
 * Writers replace the hash in place, so switching tabs leaves no history
 * entry, and notify subscribers themselves because replaceState fires no
 * event. hashchange and popstate cover edits made outside this module: a
 * typed URL, a Back press, or a router navigation to a hash.
 */
const listeners = new Set<() => void>();

/** A tab id or URL hash ("#chatgpt") back to a client, or null for anything else. */
export function parseConnectClient(value: string): ConnectClient | null {
  const candidate = value.replace(/^#/, "");

  return CONNECT_CLIENT_OPTIONS.find((option) => option.value === candidate)?.value ?? null;
}

export function subscribeToConnectHash(onChange: () => void) {
  listeners.add(onChange);
  window.addEventListener("hashchange", onChange);
  window.addEventListener("popstate", onChange);

  return () => {
    listeners.delete(onChange);
    window.removeEventListener("hashchange", onChange);
    window.removeEventListener("popstate", onChange);
  };
}

export function readConnectHash(): ConnectClient | null {
  return parseConnectClient(window.location.hash);
}

/** The server has no hash, which keeps the first client render identical to the server's. */
export function readServerConnectHash(): ConnectClient | null {
  return null;
}

export function writeConnectHash(next: ConnectClient) {
  if (readConnectHash() === next) {
    return;
  }

  // Keeping the router's own history state intact is what lets Next treat
  // this as a shallow URL update rather than a foreign history entry.
  window.history.replaceState(window.history.state, "", `#${next}`);
  listeners.forEach((listener) => listener());
}

/** Select a client's walkthrough and bring it into view. The anchor with the
    client's id sits at the top of the Connect chapter, so a deep link and this
    call land in the same place. */
export function jumpToConnectClient(next: ConnectClient) {
  writeConnectHash(next);
  document.getElementById(next)?.scrollIntoView({ behavior: "smooth" });
}
